import { Injectable, Injector } from '@angular/core';
import { Observable, ReplaySubject, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import {
    AlaStatus,
    ApplicationDto,
    ApplicationJournalRecordDto,
    ApplicationServiceProxy,
    ApplicationStatus,
    ApplicationSupplementalClaimDto,
    ApplicationType,
    ApplicationUploadFilesInput,
    AssignApplicationInput,
    AssignApplicationOutput,
    ClaStatus,
    CreateApplicationJournalRecordInput,
    EhcStatus,
    EvaluationDto,
    EvaluationInput,
    GeneralDocumentGroupDto,
    GeneralDocumentsOutput,
    GeneralFileDto,
    GeneralFileInfoDto,
    GeneralResultDocumentInput,
    LiaStatus,
    MutStatus,
    NotConsiderApplicationInputDto,
    PdfStatus,
    PersonApplicationSearchDto,
    RejectApplicationInputDto,
    RequestForSupplementalInformationInput,
    SetSupplementalInformationReminderDateInput,
    SlapStatus,
    SplStatus,
    WithdrawApplicationInputDto,
    RlaStatus
} from '@shared/service-proxies/service-proxies';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase } from '@shared/common/app-component-base';
import { CodeService } from '@app/shared/common/code/code.service';
import { SelectItem } from 'primeng/api';
import { DateTime } from 'luxon';

@Injectable()
export class ApplicationService extends AppComponentBase {

    private evaluationChangedSource = new ReplaySubject<void>(1);
    public evaluationChanged$ = this.evaluationChangedSource.asObservable();
    private transferToDossierInFlight = false;
    private lastTransferToDossierAt = 0;
    private readonly transferToDossierCooldownMs = 500;

    protected _application: ApplicationDto;
    protected _caseId = '';
    applicationStatus: SelectItem[];
    constructor(
        injector: Injector,
        protected codeService: CodeService,
        private applicationService: ApplicationServiceProxy,
    ) {
        super(injector);
    }

    get isAuthority() {
        return this.permission.isGranted('Pages.Authority.Applications');
    }

    areAllMandatoryDocumentsUploaded(documents: GeneralDocumentGroupDto[]) {
        if (documents == null) return false;
        for (var i = 0; i < documents.length; i++) {
            if (documents[i].isMandatory && documents[i].files.length === 0) {
                return false;
            }
        }
        return true;
    }

    getApplication(caseId: string): Observable<ApplicationDto> {
        this._caseId = caseId;
        return this.applicationService.getApplication(caseId).pipe(tap((output: ApplicationDto) => {
            this._application = output;
        }));
    }

    closeApplication(): void {
        this._application = null;
        this._caseId = '';
    }

    get application(): ApplicationDto {
        return this._application;
    }

    deleteApplication(): Observable<void> {
        return this.applicationService.deleteApplication(this._caseId);
    }

    isInStatus(expectedStatus: ApplicationStatus): boolean {
        return this._application.status === expectedStatus;
    }

    isOpen(state: ApplicationStatus): boolean {
        switch (state) {
            case ApplicationStatus.UnderConstruction:
            case ApplicationStatus.Submitted:
            case ApplicationStatus.UnderPreEvaluation:
            case ApplicationStatus.UnderEvaluation:
            case ApplicationStatus.SupplementalClaim:
            case ApplicationStatus.Waitlist:
                return true;
            case ApplicationStatus.FormallyEvaluated:
            case ApplicationStatus.Granted:
            case ApplicationStatus.Withdrawn:
            case ApplicationStatus.Denied:
            case ApplicationStatus.Revoked:
            case ApplicationStatus.NotConsidered:
                return false;
            default: return this.neverToBeCalled(state);
        }
    }

    get status(): ApplicationStatus {
        return this._application.status;
    }

    get caseId(): string {
        return this._caseId;
    }

    get type(): ApplicationType {
        return this._application.type;
    }

    getReleaseComment(): Observable<any> {
        return of(this._application.applicantComment);
    }

    assign(input: AssignApplicationInput): Observable<AssignApplicationOutput> {
        return this.applicationService.assignApplication(input);
    }

    unassign(caseId: string): Observable<void> {
        return this.applicationService.unassignApplication(caseId);
    }

    withdrawnApplication(input: WithdrawApplicationInputDto): Observable<void> {
        return this.applicationService.withdrawApplication(this.caseId, input).pipe(tap(() => {
            this._application.status = ApplicationStatus.Withdrawn;
        }));
    }

    reactivateApplication(caseId: string = this._caseId): Observable<void> {
        return this.applicationService.reactivateApplication(caseId).pipe(tap(() => {
            this._application.status = ApplicationStatus.Submitted;
        }));
    }

    notConsiderApplication(input: NotConsiderApplicationInputDto): Observable<void> {
        return this.applicationService.notConsiderApplication(this.caseId, input).pipe(tap(() => {
            this._application.status = ApplicationStatus.NotConsidered;
        }));
    }

    startPreEvaluation(caseId: string): Observable<void> {
        return this.applicationService.startPreEvaluation(caseId).pipe(tap(() => {
            this._application.status = ApplicationStatus.UnderPreEvaluation;
        }));
    }

    startEvaluation(caseId: string): Observable<void> {
        return this.applicationService.startEvaluation(caseId).pipe(tap(() => {
            this._application.status = ApplicationStatus.UnderEvaluation;
        }));
    }

    getEvaluation(step: number): EvaluationDto {
        if ((this._application.status !== ApplicationStatus.UnderPreEvaluation && this._application.status !== ApplicationStatus.UnderEvaluation)
            || this.application.evaluations == null) {
            return null;
        }

        return this.application.evaluations.find(e => e.step === step);
    }

    getEvaluationForSupplementalClaim(step: number): EvaluationDto {
        if (this._application.status !== ApplicationStatus.SupplementalClaim) {
            return null;
        }

        return this.application.evaluations.find(e => e.step === step);
    }

    getSupplementalClaimTemplate(): string {
        return this.application.emailTemplate.supplementalClaimTemplate;
    }

    reviewActionPossible(): boolean {
        switch (this._application.status) {
            case ApplicationStatus.FormallyEvaluated:
            case ApplicationStatus.Granted:
            case ApplicationStatus.Denied:
            case ApplicationStatus.NotConsidered:
            case ApplicationStatus.Withdrawn:
            case ApplicationStatus.Revoked:
            case ApplicationStatus.Submitted:
                return false;
            case ApplicationStatus.UnderConstruction:
            case ApplicationStatus.Submitted:
            case ApplicationStatus.UnderPreEvaluation:
            case ApplicationStatus.UnderEvaluation:
            case ApplicationStatus.SupplementalClaim:
            case ApplicationStatus.Waitlist:
                return true;
            default: return this.neverToBeCalled(this._application.status);
        }
    }

    getEvaluations(): EvaluationDto[] {
        if (this._application.status !== ApplicationStatus.UnderPreEvaluation && this._application.status !== ApplicationStatus.UnderEvaluation) {
            return null;
        }

        return this.application.evaluations;
    }

    isEvaluationFinished(): boolean {
        if (this._application.evaluations == null) {
            return false;
        }

        return this.application.evaluations.every(x => x.okayId == AppConsts.codes.evaluationStatus.ok ||
            x.okayId == AppConsts.codes.evaluationStatus.notOk ||
            x.okayId == AppConsts.codes.evaluationStatus.internalClarificationRequired);
    }

    isUnderConstruction(): boolean {
        switch (this.application?.status) {
            case ApplicationStatus.UnderConstruction:
              return true;
            default:
              return false;
            }
    }

    isUnderPreEvaluation(): boolean {
        return this.application?.status === ApplicationStatus.UnderPreEvaluation;
    }

    isUnderEvaluation(): boolean {
        return this.application?.status === ApplicationStatus.UnderEvaluation;
    }

    canReject(): boolean {
        if (!this.isAuthority) {
          return false;
        }

        switch (this.application?.status) {
        case ApplicationStatus.Submitted:
        case ApplicationStatus.UnderEvaluation:
        case ApplicationStatus.UnderPreEvaluation:
        case ApplicationStatus.Waitlist:
          return true;
        default:
          return false;
        }
      }

    canNotConsider(): boolean {
        if (this.isAuthority) {
            switch (this.application?.status) {
              case ApplicationStatus.UnderPreEvaluation:
              case ApplicationStatus.UnderEvaluation:
              case ApplicationStatus.SupplementalClaim:
              case ApplicationStatus.Waitlist:
                return true;
              default:
                return false;
            }
        }

        return false;
    }

    canReactivate(): boolean {
        if (this.isAuthority) {
            switch (this.application?.status) {
              case ApplicationStatus.NotConsidered:
              case ApplicationStatus.Denied:
              case ApplicationStatus.Withdrawn:
                return true;
              default:
                return false;
            }
        }

        return false;
    }

    canWithdraw(): boolean {
        switch (this.application?.status) {
            case ApplicationStatus.UnderConstruction:
            case ApplicationStatus.Submitted:
            case ApplicationStatus.Waitlist:
                return true;
            default:
                break;
        }

        if (this.isAuthority) {
            switch (this.application?.status as ApplicationStatus) {
                case ApplicationStatus.UnderPreEvaluation:
                case ApplicationStatus.UnderEvaluation:
                case ApplicationStatus.Waitlist:
                  return true;
                  default:
                break;
            }
        }
        return false;
    }

    updateEvaluation(evaluation: EvaluationDto): Observable<void> {
        return this.applicationService.updateEvaluation(new EvaluationInput({ caseId: this._application.caseId, evaluation: evaluation })).pipe(tap(() => {
            const item = this._application.evaluations.find(e => e.step === evaluation.step);
            const index = this._application.evaluations.indexOf(item);
            this._application.evaluations[index] = evaluation;
            const okay = this.isFormallyOkay();
            this.evaluationChangedSource.next();
        }));
    }

    requestForSupplementalInformation(message: string, isReminder: boolean = false): Observable<ApplicationDto> {
        return this.applicationService.requestForSupplementalInformation(new RequestForSupplementalInformationInput({ caseId: this._application.caseId, message: message, isReminder: isReminder }))
            .pipe(tap((app) => {
                this._application = app;
            }));
    }

    setSupplementalInformationReminderDate(reminderDate: DateTime | Date | undefined): Observable<ApplicationDto> {
        reminderDate = DateTime.isDateTime(reminderDate) ? reminderDate : (reminderDate ? DateTime.fromJSDate(reminderDate) : undefined);

        return this.applicationService.setSupplementalInformationReminderDate(new SetSupplementalInformationReminderDateInput({
            caseId: this.caseId,
            reminderDate: reminderDate
        }))
        .pipe(tap((app) => {
            this._application = app;
        }));
    }

    getCurrentSupplementalClaim(): ApplicationSupplementalClaimDto {
        // The backend sorts them already
        const dto = this._application.supplementaryClaims.at(0);
        return dto;
    }

    hasUndeliveredSupplementalClaims(): boolean {
        return (this._application.status === ApplicationStatus.UnderPreEvaluation || this._application.status === ApplicationStatus.UnderEvaluation) && this.isFormallyNotOkay();
    }

    rejectApplication(input: RejectApplicationInputDto): Observable<void> {
        return this.applicationService.rejectApplication(this._application.caseId, input);
    }

    reviseApplication(): Observable<void> {
        return this.applicationService.setApplicationUnderConstruction(this._application.caseId);
    }

    isFormallyOkay(): boolean {
        return this._application.evaluations?.filter(e => e.okayId !== AppConsts.codes.evaluationStatus.ok).length === 0;
    }

    isFormallyOkayOrInternalClarificationRequired(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId !== AppConsts.codes.evaluationStatus.ok &&
            e.okayId !== AppConsts.codes.evaluationStatus.internalClarificationRequired).length === 0;
    }

    anyStepInternalClarificationRequired(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId === AppConsts.codes.evaluationStatus.internalClarificationRequired).length > 0;
    }

    anyStepNotOkay(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId === AppConsts.codes.evaluationStatus.notOk).length > 0;
    }

    isFormallyNotOkay(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId === AppConsts.codes.evaluationStatus.notOk ||
            e.okayId === AppConsts.codes.evaluationStatus.internalClarificationRequired).length > 0;
    }

    uploadResultDocument(input: GeneralResultDocumentInput): Observable<void> {
        return this.applicationService.uploadResultDocument(input);
    }

    private checkOverallEvaluationStatus(oldOkay: boolean) {
        const okay = this.isFormallyOkay();
        if (okay !== oldOkay) {
        }
    }

    transferToDossier(licenceToRenewId: number = null): Observable<void> {
        if (licenceToRenewId == null) {
            licenceToRenewId = 0;
        }

        const now = Date.now();
        if (this.transferToDossierInFlight || now - this.lastTransferToDossierAt < this.transferToDossierCooldownMs) {
            return of(null);
        }

        this.transferToDossierInFlight = true;
        this.lastTransferToDossierAt = now;
        return this.applicationService.formallyEvaluateApplication(this._application.caseId, licenceToRenewId)
            .pipe(finalize(() => {
                this.transferToDossierInFlight = false;
            }));
    }

    accept(): Observable<void> {
        return this.applicationService.acceptApplication(this._application.caseId);
    }

    getNextEvaluationStepUrl(evaluation: EvaluationDto): string {
        if (this.isEvaluationFinished() && (this.application.status === ApplicationStatus.UnderPreEvaluation || this.application.status === ApplicationStatus.UnderEvaluation)) {
            if (this.type === ApplicationType.Lia || this.type === ApplicationType.LiaRen || this.type === ApplicationType.Lia90Days) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(LiaStatus.FinishReview);
            } else if (this.type === ApplicationType.Ehc || this.type === ApplicationType.Ehc90Days || this.type === ApplicationType.EhcRen) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(EhcStatus.FinishReview);
            } else if (this.type === ApplicationType.Slap || this.type === ApplicationType.SlapRen) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(SlapStatus.FinishReview);
            } else if (this.type === ApplicationType.Alap) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(AlaStatus.FinishReview);
            } else if (this.type === ApplicationType.Tla) {
                //    return baseUrl + 'wizard/' + this.getUrlForStep(TlaStatus.FinishReview);
            } else if (this.type === ApplicationType.Rla) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(RlaStatus.FinishReview);
            } else if (this.type === ApplicationType.Abo) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(SplStatus.FinishReview);
            } else if (this.type === ApplicationType.Sub) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(SplStatus.FinishReview);
            } else if ((this.type === ApplicationType.Cla || this.type === ApplicationType.ClaRen || this.type === ApplicationType.Ola || this.type === ApplicationType.OlaRen || this.type === ApplicationType.Slas || this.type === ApplicationType.SlasRen || this.type === ApplicationType.Nla || this.type === ApplicationType.NlaRen)) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(ClaStatus.FinishReview);
            } else if (this.type === ApplicationType.ClaEhc) {
                //    return baseUrl + 'wizard/' + this.getUrlForStep(ClaEhcStatus.FinishReview);
            } else if (this.type === ApplicationType.Alas) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(AlaStatus.FinishReview);
            } else if (this.type === ApplicationType.Mut) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(MutStatus.FinishReview);
            } else if (this.type === ApplicationType.Pdf) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(PdfStatus.FinishReview);
            } else {
                return this.neverToBeCalled(this.type);
            }
        }

        for (let index = 0; index < this._application.evaluations.length; index++) {
            if ((evaluation === null || this._application.evaluations[index].order > evaluation.order) &&
                (!this._application.evaluations[index].okayId ||
                    this._application.evaluations[index].okayId !== AppConsts.codes.evaluationStatus.ok)) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(this._application.evaluations[index].step);
            }
        }

        // Return first step
        if (evaluation === null) {
            if (this.type === ApplicationType.Lia || this.type === ApplicationType.LiaRen || this.type === ApplicationType.Lia90Days) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(LiaStatus.Person);
            } else if (this.type === ApplicationType.Ehc || this.type === ApplicationType.Ehc90Days) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(EhcStatus.SelfEmployment);
            } else if (this.type === ApplicationType.EhcRen) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(EhcStatus.Person);
            } else if (this.type === ApplicationType.Slap || this.type === ApplicationType.SlapRen) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(SlapStatus.Person);
            } else if (this.type === ApplicationType.Alap) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(AlaStatus.ApplicantPerson);
            } else if (this.type === ApplicationType.Tla) {
            //    return baseUrl + 'wizard/' + this.getUrlForStep(TlaStatus.Person);
            } else if (this.type === ApplicationType.Rla) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(RlaStatus.ApplicantPerson);
            } else if (this.type === ApplicationType.Abo) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(SplStatus.Person);
            } else if (this.type === ApplicationType.Sub) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(SplStatus.Person);
            } else if ((this.type === ApplicationType.Cla || this.type === ApplicationType.ClaRen || this.type === ApplicationType.Ola || this.type === ApplicationType.OlaRen || this.type === ApplicationType.Slas || this.type === ApplicationType.SlasRen || this.type === ApplicationType.Nla || this.type === ApplicationType.NlaRen)) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(ClaStatus.Sponsorship);
            } else if (this.type === ApplicationType.ClaEhc) {
            //    return baseUrl + 'wizard/' + this.getUrlForStep(ClaEhcStatus.Sponsorship);
            } else if (this.type === ApplicationType.Alas) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(AlaStatus.ApplicantSponsorship);
            } else if (this.type === ApplicationType.Mut) {
            //    return baseUrl + 'wizard/' + this.getUrlForStep(MutStatus.ApplicantSponsorship);
            } else if (this.type === ApplicationType.Pdf) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForStep(PdfStatus.Person);
            } else {
                return this.neverToBeCalled(this.type);
            }
        }

        return null;
    }

    getResultDocuments(caseId = this._caseId): Observable<GeneralFileInfoDto[]> {
        return this.applicationService.getResultDocuments(caseId).pipe(map((output) => output.resultDocuments));
    }

    getResultFile(caseId: string, id: number): Observable<GeneralFileDto> {
        return this.applicationService.getResultFile(caseId, id);
    }

    getHistoryStatusDocument(caseId: string, id: number): Observable<GeneralFileDto> {
        return this.applicationService.getApplicationHistoryStatusDocument(caseId, id);
    }

    getApplicationAuthorityHistoryDocument(caseId: string, id: number): Observable<GeneralFileDto> {
        return this.applicationService.getApplicationAuthorityHistoryDocument(caseId, id);
    }

    getUrlForStep(step: number): string {
        return null;
    }

    deleteResultDocument(docId: number): Observable<void> {
        return this.applicationService.deleteResultDocument(this.caseId, docId);
    }

    getCssClassForStatusId(statusId: ApplicationStatus, prefix: string): string {
        if (statusId === ApplicationStatus.Granted) {
            return prefix + 'success';
        } else if (statusId === ApplicationStatus.Withdrawn || statusId === ApplicationStatus.NotConsidered) {
            return prefix + 'secondary';
        } else if (statusId === ApplicationStatus.Denied) {
            return prefix + 'danger';
        } else if (statusId === ApplicationStatus.SupplementalClaim) {
            return prefix + 'warning';
        }

        return prefix + 'secondary';
    }

    getStatusDescription(statusId: ApplicationStatus) {
        if (!this.applicationStatus) {
            this.applicationStatus = this.codeService.getEnumForDropdown('ApplicationStatus', ApplicationStatus, false);
        }

        if (!abp.auth.isGranted('Pages.Authority.Applications') &&
            (statusId === ApplicationStatus.UnderEvaluation || statusId === ApplicationStatus.FormallyEvaluated)) {
            return this.l('MySironaLiaApplicationStatusFormallyEvaluated');
        } else {
            return this.le('applicationStatus', statusId);
        }
    }

    displaySubmittedCount(x: ApplicationDto | PersonApplicationSearchDto) : string {
        if (x.status === ApplicationStatus.Submitted && x.submittedCount > 1) {
            return `(${x.submittedCount})`;
        }
        return '';
    }

    setStepToReview(stepId: number) {
        // update warning mark from supplemental claim to okay
        if (this._application.status === ApplicationStatus.SupplementalClaim) {
            const step = this._application.evaluations.find(x => x.step === stepId);
            if (step.okayId === AppConsts.codes.evaluationStatus.ok ||
                step.okayId === AppConsts.codes.evaluationStatus.notOk ||
                step.okayId === AppConsts.codes.evaluationStatus.internalClarificationRequired) {
                this.applicationService.setStepToReview(this._application.caseId, stepId).subscribe(() => {
                    const step = this._application.evaluations.find(x => x.step === stepId);
                    step.okayId = null;
                });
            }
        }
    }

    getSupplementalClaimAnnexFilesOfStep(applicationWizardStep: number, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.applicationService.getSupplementalClaimAnnexFilesOfStep(caseId, applicationWizardStep).pipe(tap((output: GeneralDocumentsOutput) => {
            this.application.authorityDocuments = output.documents;
        })).pipe(map((output: GeneralDocumentsOutput) => output.documents));
    }

    getAuthorityFile(fileId: number): Observable<GeneralFileDto> {
        return this.applicationService.getAuthorityFile(this._caseId, fileId)
    }

    uploadAuthorityFiles(input: ApplicationUploadFilesInput, wizardStep: number): Observable<GeneralFileInfoDto[]> {
        input.caseId = this._caseId;
        return this.applicationService.uploadAuthorityFiles(wizardStep, input);
    }

    deleteAuthorityFile(fileId: number): Observable<void> {
        return this.applicationService.deleteAuthorityFile(fileId, this._caseId);
    }

    deleteAuthorityFiles(wizardStep: number): Observable<void> {
        return this.applicationService.deleteAuthorityFiles(wizardStep, this._caseId);
    }

    setApplicationsInSubmitted(caseIds: string[]): Observable<void> {
        return this.applicationService.setApplicationsInSubmitted(caseIds);
    }

    getJournalRecords(caseId: string): Observable<ApplicationJournalRecordDto[]> {
        return this.applicationService.getJournalRecords(caseId);
    }

    getLatestApplicationJournalRecord(caseId: string, wizardStep: number): Observable<ApplicationJournalRecordDto> {
        return this.applicationService.getLatestApplicationJournalRecord(caseId, wizardStep);
    }

    createJournalRecord(input: CreateApplicationJournalRecordInput): Observable<ApplicationJournalRecordDto> {
        return this.applicationService.createJournalRecord(input);
    }

    deleteJournalRecord(caseId: string, recordId: number): Observable<void> {
        return this.applicationService.deleteJournalRecord(caseId, recordId);
    }

    protected getNextStep(path: string): string {
        const url = this.getFullCaseUrl() + 'wizard/' + path;
        return url;
    }

    getFullApplicationBaseUrl(): string {
        let url;
        switch (this.type) {
            case ApplicationType.Cla:
            case ApplicationType.ClaRen:
            case ApplicationType.Ola:
            case ApplicationType.OlaRen:
            case ApplicationType.Slas:
            case ApplicationType.SlasRen:
            case ApplicationType.Nla:
            case ApplicationType.NlaRen:
            case ApplicationType.ClaEhc:
            case ApplicationType.Alas:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/sponsorship' : AppConsts.applicantBaseUrl;
                break;
            case ApplicationType.Mut:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/mutation' : AppConsts.applicantBaseUrl;
                break;
            case ApplicationType.Lia:
            case ApplicationType.LiaRen:
            case ApplicationType.Lia90Days:
            case ApplicationType.Ehc:
            case ApplicationType.Ehc90Days:
            case ApplicationType.EhcRen:
            case ApplicationType.Slap:
            case ApplicationType.SlapRen:
            case ApplicationType.Alap:
            case ApplicationType.Tla:
            case ApplicationType.Rla:
            case ApplicationType.Abo:
            case ApplicationType.Sub:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/person' : AppConsts.applicantBaseUrl;
                break;
            case ApplicationType.Pdf:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/pdf-applications' : AppConsts.applicantBaseUrl;
                break;
            default: this.neverToBeCalled(this.type);
        }

        return url;
    }

    getFullCaseUrl(): string {
        const url = this.getFullApplicationBaseUrl() + `/${this.getNavigationContext()}/${this.caseId}/`;
        return url;
    }

    getNavigationContext(type: ApplicationType = this.type): string {
        switch (type) {
            case ApplicationType.Lia:
            case ApplicationType.LiaRen:
            case ApplicationType.Lia90Days:
                return 'lia';
            case ApplicationType.Ehc:
            case ApplicationType.EhcRen:
            case ApplicationType.Ehc90Days:
                return 'ehc';
            case ApplicationType.Rla:
                return 'rla';
            case ApplicationType.Alap:
            case ApplicationType.Alas:
                return 'ala';
            case ApplicationType.Tla:
                return 'tla';
            case ApplicationType.Slap:
            case ApplicationType.SlapRen:
                return 'slap';

            case ApplicationType.Cla:
            case ApplicationType.ClaRen:
            case ApplicationType.Slas:
            case ApplicationType.SlasRen:
            case ApplicationType.Nla:
            case ApplicationType.NlaRen:
            case ApplicationType.Ola:
            case ApplicationType.OlaRen:
                return 'cla';
            case ApplicationType.ClaEhc:
                return 'claEhc';
            case ApplicationType.Abo:
            case ApplicationType.Sub:
                return 'spl';

            case ApplicationType.Mut:
                return 'mut';
            case ApplicationType.Pdf:
                return 'pdf';
            default: this.neverToBeCalled(type);
        }
    }

    getStartPage(type: ApplicationType = this.type): string {
        switch (type) {
            case ApplicationType.Alap:
                return 'wizard/applicant-person';

                case ApplicationType.Alas:
                return 'wizard/applicant-sponsorship';

            case ApplicationType.Lia:
            case ApplicationType.LiaRen:
            case ApplicationType.Lia90Days:
            case ApplicationType.Ehc:
            case ApplicationType.EhcRen:
            case ApplicationType.Ehc90Days:
            case ApplicationType.Rla:
            case ApplicationType.Tla:
            case ApplicationType.Slap:
            case ApplicationType.SlapRen:
            case ApplicationType.Cla:
            case ApplicationType.ClaRen:
            case ApplicationType.Slas:
            case ApplicationType.SlasRen:
            case ApplicationType.Nla:
            case ApplicationType.NlaRen:
            case ApplicationType.Ola:
            case ApplicationType.OlaRen:
            case ApplicationType.ClaEhc:
            case ApplicationType.Abo:
            case ApplicationType.Sub:
            case ApplicationType.Mut:
            case ApplicationType.Pdf:
                return 'wizard';
            default: this.neverToBeCalled(type);
        }
    }

    getFullStartPageUrl(): string {
        const url = this.getFullCaseUrl() + this.getStartPage();
        return url;
    }
}
